/**
 * CORE-1071 — галочка «Условий реферальной программы» в виджете участия
 * (решение Марии 03.11: отдельный документ, принимается галочкой там, где
 * человеку предлагают участвовать). Без React Native — правила проверяются под jest.
 *
 * Нужен ли документ и какая редакция действует, решает сервер
 * (`termsDocumentRequired` / `termsDocument` в `GET /api/referral/me`, флаг
 * `referral_terms_document`). Клиент только показывает галочку и называет
 * при подключении ровно ту редакцию, что получил. Флаг выключен — поле
 * `termsDocumentRequired` ложно или отсутствует, и виджет такой же, как был.
 */
import type { ReferralIdentity, ReferralTermsDocument } from '../../types/referral';

export type ReferralTermsGate =
  /** Документ не нужен: подключение как раньше, одной кнопкой. */
  | { kind: 'none' }
  /** Нужен, но редакции для контура человека в реестре нет — подключиться пока нельзя. */
  | { kind: 'unavailable' }
  /** Нужна галочка под этой редакцией. */
  | { kind: 'checkbox'; document: ReferralTermsDocument };

export function referralTermsGate(identity: Pick<ReferralIdentity, 'termsDocumentRequired' | 'termsDocument'>): ReferralTermsGate {
  if (!identity.termsDocumentRequired) return { kind: 'none' };
  return identity.termsDocument ? { kind: 'checkbox', document: identity.termsDocument } : { kind: 'unavailable' };
}

/** Можно ли нажать «Подключиться»: без галочки участие не включается. */
export function canEnroll(gate: ReferralTermsGate, accepted: boolean): boolean {
  if (gate.kind === 'none') return true;
  if (gate.kind === 'unavailable') return false;
  return accepted;
}

/** Что назвать серверу при подключении: редакция — только когда её требуют. */
export function enrollDocument(gate: ReferralTermsGate):
  Omit<ReferralTermsDocument, 'effectiveFrom'> | undefined {
  if (gate.kind !== 'checkbox') return undefined;
  const { documentId, version, variant, sha256 } = gate.document;
  return { documentId, version, variant, sha256 };
}

/**
 * CORE-2078 — повторное принятие (механизм CORE-1568): подключённому, который не
 * принимал действующую редакцию, виджет при входе снова показывает галочку.
 * Решает сервер (`termsReacceptRequired`); галочку показываем, только если есть
 * что принимать.
 */
export function needsReaccept(
  identity: Pick<ReferralIdentity, 'enrolled' | 'termsReacceptRequired' | 'termsDocumentRequired' | 'termsDocument'>,
): boolean {
  return !!identity.enrolled && !!identity.termsReacceptRequired && referralTermsGate(identity).kind === 'checkbox';
}
